import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { fetchClientAccountDetail, fetchClientAccounts } from "@/services/clientAccounts";
import type {
    ClientAccountDetailFilters,
    ClientAccountDetailResponse,
    ClientAccountFilters,
    ClientAccountsResponse
} from "@/types/clientAccounts";

import { useClientContextKey } from "./useClientContextKey";

type NormalizedFilters = Record<string, string>;

function normalizeFilters(filters?: ClientAccountFilters | ClientAccountDetailFilters): NormalizedFilters {
    if (!filters) return {};

    return Object.fromEntries(
        Object.entries(filters).filter(([, value]) => typeof value === "string" && value.length > 0)
    ) as NormalizedFilters;
}

interface UseClientAccountsReturn {
    data: ClientAccountsResponse | null;
    isLoading: boolean;
    error: Error | null;
}

export function useClientAccounts(filters?: ClientAccountFilters): UseClientAccountsReturn {
    const contextKey = useClientContextKey();
    const queryFilters = normalizeFilters(filters);
    const { data, isLoading, error } = useQuery<ClientAccountsResponse, Error>({
        queryKey: ["client-accounts", contextKey, queryFilters],
        queryFn: () => fetchClientAccounts(queryFilters),
        placeholderData: keepPreviousData,
        staleTime: 2 * 60 * 1000,
        retry: false
    });

    return {
        data: data ?? null,
        isLoading,
        error
    };
}

interface UseClientAccountDetailReturn {
    data: ClientAccountDetailResponse | null;
    isLoading: boolean;
    error: Error | null;
}

export function useClientAccountDetail(
    accountUuid: string | null,
    filters?: ClientAccountDetailFilters
): UseClientAccountDetailReturn {
    const contextKey = useClientContextKey();
    const queryFilters = normalizeFilters(filters);
    const { data, isLoading, error } = useQuery<ClientAccountDetailResponse, Error>({
        queryKey: ["client-account", contextKey, accountUuid ?? "none", queryFilters],
        queryFn: () => fetchClientAccountDetail(accountUuid as string, queryFilters),
        enabled: Boolean(accountUuid),
        placeholderData: keepPreviousData,
        staleTime: 2 * 60 * 1000,
        retry: false
    });

    return {
        data: data ?? null,
        isLoading,
        error
    };
}
